/**
 * Quick Edit: upload CV → AI extract/rewrite → edit dashboard + template.
 *
 * If opened from an ATS report (`?fromCv=`), reuses that stored file.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useRequireAuth } from '../contexts/AuthGateContext';
import Seo from '../components/Seo';
import AnalyzingProgress from '../components/AnalyzingProgress';
import {
  trackCvExtractOk,
  trackCvUpload,
  trackQuickEditAutostartFail,
} from '../utils/analytics';
import { hasAiConsent, rememberAiConsent } from '../utils/aiConsent';
import { requireCookieChoice } from '../utils/cookieConsent';
import { recalledJobPosition, rememberJobPosition } from '../utils/jobPosition';
import { usePlanLimitPrompt } from '../hooks/usePlanLimitPrompt';
import './FlowPages.css';

const autoStartedFromCv = new Set();

const autostartFailReason = (error) => {
  const errorData = error?.response?.data;
  const code = errorData?.error_code;
  if (code === 'ai_consent_required') return 'consent_required';
  if (code === 'pdf_no_text') return 'pdf_no_text';
  if (errorData?.error === 'No file provided') return 'missing_file';
  if (errorData?.error || errorData?.detail) return 'api_error';
  return 'unknown';
};

const fileNameFromCv = (cv) => {
  if (!cv) return '';
  if (cv.title) return cv.title;
  const path = String(cv.original_file || '');
  const parts = path.split('/').filter(Boolean);
  try {
    return decodeURIComponent(parts[parts.length - 1] || '');
  } catch (err) {
    return parts[parts.length - 1] || '';
  }
};

const QuickEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const requireAuth = useRequireAuth();
  const { handlePlanLimitError, prompt: upgradePrompt } = usePlanLimitPrompt();
  const fromCvId = (
    searchParams.get('fromCv')
    || (location.state && location.state.fromCv)
    || ''
  )
    .toString()
    .trim();
  const jobFromUrl = (searchParams.get('job') || '').trim();
  const autoStart = searchParams.get('autostart') === '1';
  const consentAlreadyGiven = Boolean(fromCvId) || hasAiConsent();
  const [storedCv, setStoredCv] = useState(null);
  const [skipStored, setSkipStored] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(() => Boolean(autoStart && fromCvId));
  const [aiConsent, setAiConsent] = useState(() => consentAlreadyGiven);
  const [jobPosition, setJobPosition] = useState(
    () => location.state?.jobPosition || jobFromUrl || recalledJobPosition(fromCvId) || ''
  );
  const fromTemplates =
    new URLSearchParams(location.search).get('from') === 'templates';
  const [handoffFailed, setHandoffFailed] = useState(false);
  const autostartFailSentRef = useRef(false);
  const usingStored = Boolean(fromCvId && !skipStored && !file);
  const titleKey = fromTemplates
    ? 'flow.rebuildTitleTemplates'
    : 'flow.rebuildTitle';
  const leadKey = usingStored
    ? 'flow.rebuildLeadReuse'
    : fromTemplates
      ? 'flow.rebuildLeadTemplates'
      : 'flow.rebuildLead';

  useEffect(() => {
    if (!fromCvId) {
      return undefined;
    }
    let cancelled = false;
    axios
      .get(`/api/cvs/${fromCvId}/`)
      .then((response) => {
        if (cancelled) return;
        if (response.data) {
          setStoredCv(response.data);
        } else {
          setBusy(false);
        }
        const fromAnalysis = (response.data?.analysis?.job_position || '').trim();
        if (fromAnalysis) {
          setJobPosition((current) => (current || '').trim() || fromAnalysis);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStoredCv(null);
          setBusy(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fromCvId]);

  const reportAutostartFail = (reason) => {
    if (!autoStart || autostartFailSentRef.current) {
      return;
    }
    autostartFailSentRef.current = true;
    trackQuickEditAutostartFail({ reason });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];

    if (!validTypes.includes(selectedFile.type)) {
      toast.error(t('flow.errFileType'));
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error(t('flow.errFileSize'));
      return;
    }

    setSkipStored(true);
    setFile(selectedFile);
  };

  const handleUploadAndAnalyze = async () => {
    const sourceId = (storedCv && storedCv.id) || fromCvId;
    if (!file && !sourceId) {
      toast.error(t('flow.errNeedFile'));
      setBusy(false);
      reportAutostartFail('missing_file');
      return;
    }
    if (!aiConsent) {
      toast.error(t('flow.errNeedConsent'));
      setBusy(false);
      reportAutostartFail('consent_required');
      return;
    }
    if (!(await requireAuth())) {
      setBusy(false);
      reportAutostartFail('auth_required');
      return;
    }

    await requireCookieChoice();

    setBusy(true);
    try {
      rememberAiConsent();
      if (fromCvId) {
        rememberJobPosition(fromCvId, jobPosition);
      }

      let analysisResponse;
      const job = jobPosition.trim();
      if (file) {
        const formData = new FormData();
        formData.append('pdf_file', file);
        formData.append('ai_consent', 'true');
        if (job) {
          formData.append('job_position', job);
        }
        analysisResponse = await axios.post('/api/cvs/analyze-pdf/', formData);
      } else {
        const payload = {
          ai_consent: true,
          source_cv_id: String(sourceId),
        };
        if (job) {
          payload.job_position = job;
        }
        analysisResponse = await axios.post(
          `/api/cvs/analyze-pdf/?source_cv_id=${encodeURIComponent(String(sourceId))}`,
          payload,
        );
      }

      trackCvUpload({ source: usingStored ? 'quick_edit_from_ats' : 'quick_edit' });
      toast.success(t('flow.analyzeOk'));
      trackCvExtractOk();
      navigate('/dashboard/edit-cv', {
        state: {
          analysisData: analysisResponse.data,
          openGallery: fromTemplates,
        },
        replace: true,
      });
    } catch (error) {
      if (fromCvId) {
        autoStartedFromCv.delete(fromCvId);
      }
      if (autoStart) {
        setHandoffFailed(true);
      }
      reportAutostartFail(autostartFailReason(error));
      console.error('Error analyzing CV:', error);
      if (await handlePlanLimitError(error)) {
        return;
      }
      const errorData = error.response?.data;
      const errorMessage =
        errorData?.error_code === 'ai_consent_required'
          ? t('flow.errNeedConsent')
          : errorData?.error_code === 'pdf_no_text' ||
              /extract text|parsing PDF|no readable text|image-only|redacted/i.test(
                String(errorData?.error || '')
              )
            ? t('flow.errPdfNoText')
            : errorData?.error === 'No file provided'
              ? t('flow.errNeedFile')
              : errorData?.error || errorData?.detail || t('flow.analyzeFail');
      toast.error(errorMessage);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!autoStart) {
      return undefined;
    }
    if (!fromCvId) {
      reportAutostartFail('missing_from_cv');
      return undefined;
    }
    if (!usingStored || !aiConsent) {
      if (!aiConsent) {
        reportAutostartFail('consent_required');
      }
      return undefined;
    }
    if (autoStartedFromCv.has(fromCvId)) {
      return undefined;
    }
    autoStartedFromCv.add(fromCvId);
    handleUploadAndAnalyze();
    return undefined;
  }, [autoStart, fromCvId, usingStored, aiConsent]);

  const canSubmit = Boolean((file || usingStored) && aiConsent && !busy);
  const shownName = file ? file.name : fileNameFromCv(storedCv);
  const atsHandoff = Boolean(autoStart && fromCvId && usingStored && !handoffFailed);

  if (atsHandoff) {
    return (
      <div className="container flow-page">
        <Seo
          title={t('flow.rewriteFromAtsTitle')}
          description={t('flow.rewriteFromAtsLead')}
          path="/dashboard/quick-edit"
          noindex
        />
        <div className="flow-card">
          <h1 className="flow-title">{t('flow.rewriteFromAtsTitle')}</h1>
          <p className="flow-lead">{t('flow.rewriteFromAtsLead')}</p>
          {shownName ? (
            <p className="flow-file-meta">{t('flow.analyzeFile', { name: shownName })}</p>
          ) : null}
          <AnalyzingProgress
            active={busy}
            title={t('flow.generateProgressTitle')}
            steps={[
              t('flow.generateStep1'),
              t('flow.generateStep2'),
              t('flow.generateStep3'),
              t('flow.analyzeStep4'),
            ]}
          />
        </div>
        {upgradePrompt}
      </div>
    );
  }

  return (
    <div className="container flow-page">
      <Seo title={t(titleKey)} description={t(leadKey)} path="/dashboard/quick-edit" noindex />
      <div className="flow-card">
        <Link to="/dashboard" className="flow-back">
          ← {t('dashboard.back')}
        </Link>
        <h1 className="flow-title">{t(titleKey)}</h1>
        <p className="flow-lead">{t(leadKey)}</p>
        <AnalyzingProgress
          active={busy}
          title={t('flow.analyzeProgressTitle')}
          steps={[
            t('flow.analyzeStep1'),
            t('flow.analyzeStep2'),
            t('flow.analyzeStep3'),
            t('flow.analyzeStep4'),
          ]}
        />

        <div className="flow-upload-box">
          <h2 className="flow-upload-box__title">
            {usingStored ? t('flow.usingExistingFile') : t('flow.uploadBoxTitle')}
          </h2>
          {usingStored ? (
            <p className="flow-upload-box__hint">{t('flow.usingExistingHint')}</p>
          ) : (
            <p className="flow-upload-box__hint">{t('flow.formats')}</p>
          )}

          <input
            type="file"
            id="cv-rebuild-upload"
            accept=".pdf,.docx,.doc,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleFileChange}
            className="sr-only"
          />
          {!usingStored ? (
            <label htmlFor="cv-rebuild-upload" className="btn btn-primary">
              {t('flow.chooseFile')}
            </label>
          ) : null}

          {file || usingStored ? (
            <div className="flow-file" role="status">
              <div>
                <strong>{t('flow.selected')}</strong> {shownName}
                {file ? (
                  <span className="flow-file__size">
                    {' '}
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                ) : null}
              </div>
              <div className="flow-file__actions">
                {usingStored ? (
                  <label htmlFor="cv-rebuild-upload" className="btn btn-outline">
                    {t('flow.chooseOtherFile')}
                  </label>
                ) : null}
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setFile(null);
                    if (storedCv) {
                      setSkipStored(true);
                    }
                  }}
                  aria-label={t('flow.removeFile')}
                >
                  {t('flow.remove')}
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div className="form-group" style={{ marginBottom: '1.25rem', textAlign: 'left' }}>
          <label htmlFor="cv-rebuild-job">{t('flow.helpJobLabel')}</label>
          <input
            id="cv-rebuild-job"
            type="text"
            value={jobPosition}
            onChange={(event) => setJobPosition(event.target.value)}
            placeholder={t('flow.helpJobPlaceholder')}
            maxLength={200}
            disabled={busy}
          />
          <p className="flow-field__hint">
            {fromCvId && jobPosition.trim() ? t('flow.jobHintReuse') : t('flow.jobHint')}
          </p>
        </div>

        {consentAlreadyGiven ? (
          <p className="flow-consent-note">{t('flow.aiConsentRemembered')}</p>
        ) : (
          <label className="flow-consent" htmlFor="ai-consent">
            <input
              id="ai-consent"
              type="checkbox"
              checked={aiConsent}
              onChange={(e) => setAiConsent(e.target.checked)}
            />
            <span>{t('flow.aiConsent')}</span>
          </label>
        )}

        <div className="flow-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUploadAndAnalyze}
            disabled={!canSubmit}
            aria-busy={busy}
          >
            {busy ? t('flow.analyzing') : t('flow.analyzeAndEdit')}
          </button>
        </div>

        <div className="flow-next">
          <h2 className="flow-next__title">{t('flow.whatsNext')}</h2>
          <ol>
            <li>{t('flow.next1')}</li>
            <li>{t('flow.next2')}</li>
            <li>{t('flow.next3')}</li>
            <li>{t('flow.next4')}</li>
          </ol>
        </div>
      </div>
      {upgradePrompt}
    </div>
  );
};

export default QuickEdit;
